import React, { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MemberCombobox } from "@/components/support/MemberCombobox";
import { MoreHorizontal, Plus, Users2 } from "lucide-react";

function AddToList({ role, candidates, members, orgId, onDone }) {
  const [open, setOpen] = useState(false);
  const existing = new Set(members.map((m) => m.user_id));
  if (!open) return <button onClick={() => setOpen(true)} className="text-xs font-medium text-brand hover:underline inline-flex items-center gap-1 shrink-0" data-testid={`team-add-${role}`}><Plus className="w-3 h-3" />Add</button>;
  const add = async (uid) => {
    if (!uid) return;
    try { await api.post("/recruiting-team/members", { organization_id: orgId, user_id: uid, role }); toast.success("Added to the team"); setOpen(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="basis-full mt-1" data-testid={`team-add-picker-${role}`}>
      <MemberCombobox members={candidates.filter((c) => !existing.has(c.member_id))} value={null} onChange={add} testId={`team-add-select-${role}`}
        placeholder="Search website members" emptyText="No matching website members." clearable={false} ariaLabel="Add website member" />
      <button onClick={() => setOpen(false)} className="text-[11px] text-slate-400 mt-1">Cancel</button>
    </div>
  );
}

function MemberMenu({ m, role, groups, onDone }) {
  const move = async (to) => {
    try { await api.patch(`/recruiting-team/members/${m.membership_id}`, { role: to }); toast.success("Moved"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${m.name} from this list?`)) return;
    try { await api.delete(`/recruiting-team/members/${m.membership_id}`); toast.success("Removed"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-1 rounded hover:bg-slate-100 text-slate-400" aria-label={`Manage ${m.name}`} data-testid={`team-member-menu-${m.membership_id}`}><MoreHorizontal className="w-4 h-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white">
        <DropdownMenuLabel className="text-xs text-slate-500">Move to</DropdownMenuLabel>
        {groups.filter((g) => g.role !== role).map((g) => (
          <DropdownMenuItem key={g.role} onClick={() => move(g.role)} data-testid={`team-move-${m.membership_id}-${g.role}`}>{g.label}</DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={remove} className="text-rose-600" data-testid={`team-remove-${m.membership_id}`}>Remove from list</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Recruiting team roster for one group, backed by memberships. */
export function RecruitingTeamPanel({ orgs }) {
  const district = localStorage.getItem("tr_district") || "";
  const [orgId, setOrgId] = useState(district && orgs.some((o) => o.id === district) ? district : orgs[0]?.id || "");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!orgId) return;
    api.get("/recruiting-team", { params: { organization_id: orgId } }).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); setData(null); });
  }, [orgId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!orgId && orgs[0]) setOrgId(orgs[0].id);
  }, [orgs, orgId]);
  if (!orgs.length) return null;
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm mb-4" data-testid="recruiting-team-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <h3 className="font-display font-semibold text-sm text-slate-900 flex items-center gap-2"><Users2 className="w-4 h-4 text-brand" />Recruiting Team</h3>
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger className="h-8 w-60 text-sm" data-testid="team-org-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-3">
            {data.groups.map((g) => (
              <div key={g.role} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3" data-testid={`team-list-${g.role}`}>
                <div className="flex flex-wrap items-center justify-between gap-x-2 mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{g.label} <span className="text-slate-400">({g.members.length})</span></p>
                  {data.can_manage && <AddToList role={g.role} candidates={data.candidates} members={g.members} orgId={orgId} onDone={load} />}
                </div>
                {g.members.length ? (
                  <ul className="space-y-1">
                    {g.members.map((m) => (
                      <li key={m.membership_id} className="flex items-center justify-between gap-2 rounded-md bg-white border border-slate-100 px-2 py-1.5" data-testid={`team-member-${m.membership_id}`}>
                        <span className="min-w-0"><span className="block text-sm text-slate-800 truncate">{m.name}</span><span className="block text-[11px] text-slate-400 truncate">{m.email}</span></span>
                        {data.can_manage && <MemberMenu m={m} role={g.role} groups={data.groups} onDone={load} />}
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-slate-400">No one yet.</p>}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Only Recruiting Managers and Internal Recruiters can be assigned as a candidate's recruiter.</p>
        </>
      )}
    </section>
  );
}
